import medicamentApi from '@/api/_medicament'
import protocoleApi from '@/api/consultations/_protocole-soins'
import protocolesTypesApi from '@/api/consultations/_protocoles-types'
import finance from '@/store/mods/finances/finance'
import { minutesDeSoin, quantiteTotale, doseCliniqueParPrise, construirePlanExpress, saisieDepuisPlan } from './plan-soins-express-payload'

const nouvelleLigne = () => ({ medicamentId: '', recherche: '', horsCatalogue: false, listeOuverte: false, survolIndex: -1, voieId: '', dose: 1, doseClinique: '', rythmeMode: 'unique', intervalleValeur: '', intervalleUnite: 'heure', dureeValeur: '', dureeUnite: 'jour', debutMode: 'imprecis', debutValeur: '', debutUnite: 'heure' })
const nouvelActe = () => ({ acteId: '', nombre: 1 })

export default {
    name: 'plan-soins-express',
    props: {
        open_toggler: Number,
        consultation: Object,
        evolution_id: Number,
        user_info: Object,
        patient: Object,
        info_assurance_patient: Object,
        // Recette du 03/10 : { protocole_id, jeton } pour modifier un plan non réglé.
        edition_demande: Object
    },
    data() {
        return {
            ouvert: false, chargement: false, enregistrement: false, transmissionCaisse: false, enregistre: false, erreur: '',
            admissions: [], chambres: [], observations: [], medicaments: [], voies: [], actesDisponibles: [],
            admissionId: '', chambreId: '', observationId: '', joursChambre: 1, joursObservation: 1,
            lignes: [nouvelleLigne()], actes: [], consigne: '', modeles: [], modeleId: '',
            rechercheModele: '', favoriOuvert: false, nomFavori: '', favoriConsigne: false, favoriEnregistrement: false, favoriErreur: '',
            brouillonPret: false, brouillonStatut: '', brouillonErreur: '', brouillonVersion: 0,
            brouillonSauveVersion: 0, brouillonTimer: null, brouillonPromise: null,
            ressourcesCle: '', ressourcesPromise: null, ressourcesPretes: null, ouvertureVersion: 0,
            saisiePendantChargement: false, brouillonEnConflit: null,
            // Modification d'un plan non réglé : il est remplacé à l'enregistrement, sans brouillon.
            remplaceProtocoleId: 0, referenceRemplacee: '', editionPrete: false,
            // Mise à jour de la caisse restée incomplète : { retirer: [{uid}], ajouter: [op] }.
            caisseEnAttente: null
        }
    },
    computed: {
        admission() { return this.admissions.find(item => String(item.admission_id) === String(this.admissionId)) },
        chambre() { return this.chambres.find(item => String(item.admission_id) === String(this.chambreId)) },
        observation() { return this.observations.find(item => String(item.admission_id) === String(this.observationId)) },
        modelesFiltres() {
            const terme = String(this.rechercheModele || '').trim().toLocaleLowerCase()
            return this.modeles.filter(item => !terme || String(item.nom).toLocaleLowerCase().includes(terme))
                .sort((a, b) => Number(b.personnel) - Number(a.personnel) || Number(b.utilisations || 0) - Number(a.utilisations || 0) || String(a.nom).localeCompare(String(b.nom)))
                .slice(0, terme ? 8 : 5)
        },
        resumePrescription() {
            const medicaments = this.lignes.filter(ligne => ligne.medicamentId || ligne.horsCatalogue).map(ligne => {
                const nom = (this.medicamentDe(ligne) || {}).libelle || ligne.recherche
                const dose = this.doseAutomatique(ligne) || ligne.doseClinique || `${ligne.dose} ${this.uniteDose(ligne)}`
                const voie = (this.voieDe(ligne) || {}).designation || 'voie à préciser'
                const rythme = ligne.rythmeMode === 'repete' ? `toutes les ${ligne.intervalleValeur || '?'} ${ligne.intervalleUnite}(s) pendant ${ligne.dureeValeur || '?'} ${ligne.dureeUnite}(s)` : 'une prise'
                return `${nom} · ${dose} · ${voie} · ${rythme}`
            })
            const actes = this.actes.filter(ligne => ligne.acteId).map(ligne => `${(this.acteDe(ligne) || {}).acte || 'Acte à vérifier'} · ${ligne.nombre} fois`)
            return medicaments.concat(actes)
        },
        brouillon() { return {
            admissionId: this.admissionId, chambreId: this.chambreId, observationId: this.observationId,
            joursChambre: this.joursChambre, joursObservation: this.joursObservation,
            lignes: this.lignes.map(ligne => ({
                medicamentId: ligne.medicamentId, recherche: ligne.recherche, horsCatalogue: ligne.horsCatalogue, voieId: ligne.voieId, dose: ligne.dose, doseClinique: ligne.doseClinique,
                rythmeMode: ligne.rythmeMode, intervalleValeur: ligne.intervalleValeur, intervalleUnite: ligne.intervalleUnite,
                dureeValeur: ligne.dureeValeur, dureeUnite: ligne.dureeUnite, debutMode: ligne.debutMode,
                debutValeur: ligne.debutValeur, debutUnite: ligne.debutUnite
            })), actes: this.actes, consigne: this.consigne
        } }
    },
    watch: {
        open_toggler() { this.ouvrir() },
        edition_demande(demande) { if (demande && Number(demande.protocole_id) > 0) this.ouvrirEdition(Number(demande.protocole_id)) },
        'consultation.id'() { this.prechargerOuverture().catch(() => {}) },
        evolution_id() { this.prechargerOuverture().catch(() => {}) },
        brouillon: { deep: true, handler() { this.planifierBrouillon() } }
    },
    mounted() { this.prechargerOuverture().catch(() => {}) },
    beforeDestroy() { if (this.brouillonTimer) clearTimeout(this.brouillonTimer) },
    methods: {
        prechargerOuverture() {
            const consultationId = Number((this.consultation || {}).id)
            const evolutionId = Number(this.evolution_id)
            // Évolution de complément pas encore créée (identifiant provisoire négatif) : rien à précharger.
            if (!consultationId || !(evolutionId > 0)) return Promise.reject(new Error('Consultation indisponible.'))
            const cle = `${consultationId}:${evolutionId}`
            if (this.ressourcesCle === cle && this.ressourcesPromise) return this.ressourcesPromise
            this.ressourcesCle = cle
            this.ressourcesPretes = null
            const requetes = [
                this.$store.dispatch('getListeTypeAdmission', 'admission'),
                this.$store.dispatch('getListeTypeAdmission', 'chambre'),
                this.$store.dispatch('getListeTypeAdmission', 'observation'),
                this.$store.dispatch('getListeModeAdministration'),
                medicamentApi.getListeMedicamentDisponible({ avecRuptures: true }),
                this.$store.dispatch('getTarifActes', 'acte'),
                protocolesTypesApi.listePourSoins().catch(() => []),
                protocoleApi.lireBrouillonPlanSoins(consultationId, evolutionId)
                    .catch(() => { throw new Error('Impossible de récupérer le brouillon. Réouvrez le formulaire pour éviter toute perte de saisie.') })
            ].map((requete, index) => Promise.resolve(requete).then(valeur => {
                if (this.ressourcesCle === cle && index !== 7) this.appliquerCatalogue(index, valeur)
                return valeur
            }))
            const requete = Promise.all(requetes)
            this.ressourcesPromise = requete.then(resultat => {
                if (this.ressourcesCle === cle) this.ressourcesPretes = resultat
                return resultat
            }).catch(error => {
                if (this.ressourcesCle === cle) this.ressourcesPromise = null
                throw error
            })
            return this.ressourcesPromise
        },
        appliquerCatalogue(index, valeur) {
            const noms = ['admissions', 'chambres', 'observations', 'voies', 'medicaments', 'actesDisponibles', 'modeles']
            if (noms[index]) this[noms[index]] = Array.isArray(valeur) ? valeur : []
        },
        noterSaisie(event) {
            if (event && event.type === 'click' && (!event.target || !event.target.closest('button'))) return
            if (this.ouvert && !this.brouillonPret) this.saisiePendantChargement = true
        },
        resoudreConflit(restaurer) {
            if (!this.brouillonEnConflit) return
            if (restaurer) this.restaurerBrouillon(this.brouillonEnConflit)
            this.brouillonEnConflit = null
            this.brouillonPret = true
            if (!restaurer) this.planifierBrouillon()
        },
        async ouvrir(options = {}) {
            const version = ++this.ouvertureVersion
            const cle = `${Number((this.consultation || {}).id)}:${Number(this.evolution_id)}`
            const ressourcesPrechargees = this.ressourcesCle === cle ? this.ressourcesPretes : null
            this.remplaceProtocoleId = Number(options.remplace) || 0
            this.referenceRemplacee = ''
            this.editionPrete = false
            this.caisseEnAttente = null
            this.brouillonPret = false
            this.ouvert = true
            this.erreur = ''
            this.enregistre = false
            this.lignes = [nouvelleLigne()]
            this.actes = []
            this.admissionId = ''
            this.chambreId = ''
            this.observationId = ''
            this.joursChambre = 1
            this.joursObservation = 1
            this.consigne = ''
            this.modeleId = ''
            this.rechercheModele = ''
            this.favoriOuvert = false
            this.nomFavori = ''
            this.favoriErreur = ''
            this.brouillonErreur = ''
            this.brouillonStatut = ''
            this.brouillonVersion = 0
            this.brouillonSauveVersion = 0
            this.saisiePendantChargement = false
            this.brouillonEnConflit = null
            this.chargement = !ressourcesPrechargees
            try {
                const [admissions, chambres, observations, voies, medicaments, actes, modeles, brouillon] =
                    ressourcesPrechargees || await this.prechargerOuverture()
                if (version !== this.ouvertureVersion || !this.ouvert) return
                this.admissions = Array.isArray(admissions) ? admissions : []
                this.chambres = Array.isArray(chambres) ? chambres : []
                this.observations = Array.isArray(observations) ? observations : []
                this.voies = Array.isArray(voies) ? voies : []
                this.medicaments = Array.isArray(medicaments) ? medicaments : []
                this.actesDisponibles = Array.isArray(actes) ? actes : []
                this.modeles = Array.isArray(modeles) ? modeles : []
                if (brouillon && typeof brouillon === 'object' && !this.remplaceProtocoleId) {
                    if (this.saisiePendantChargement) {
                        this.brouillonEnConflit = brouillon
                        this.brouillonStatut = 'Un brouillon antérieur a été retrouvé.'
                    } else this.restaurerBrouillon(brouillon)
                }
            } catch (error) {
                if (version !== this.ouvertureVersion || !this.ouvert) return
                this.erreur = error.message && error.message.includes('brouillon') ? error.message : 'Les référentiels de soins sont indisponibles. Réessayez.'
            } finally {
                if (version === this.ouvertureVersion && this.ouvert) {
                    this.chargement = false
                    this.$nextTick(() => {
                        if (version === this.ouvertureVersion && this.ouvert) {
                            this.brouillonPret = !this.erreur && !this.brouillonEnConflit
                            if (this.brouillonPret && this.saisiePendantChargement) this.planifierBrouillon()
                        }
                    })
                }
            }
        },
        // Recette du 03/10 : rouvre un plan non réglé dans ce formulaire. Le serveur confirme
        // qu'il est encore modifiable (auteur, caisse, installation, soins) et renvoie son contenu.
        async ouvrirEdition(protocoleId) {
            await this.ouvrir({ remplace: protocoleId })
            if (!this.ouvert || this.erreur || this.remplaceProtocoleId !== Number(protocoleId)) return
            const version = this.ouvertureVersion
            this.chargement = true
            try {
                const etat = await protocoleApi.etatModificationPlanSoins(protocoleId)
                if (version !== this.ouvertureVersion) return
                if (!etat || !etat.success || !etat.modifiable || !etat.plan) {
                    this.erreur = (etat && etat.motif) || 'Ce plan ne peut plus être modifié.'
                    return
                }
                this.restaurerBrouillon(saisieDepuisPlan(etat.plan, this.voies))
                this.referenceRemplacee = etat.plan.reference || ''
                this.brouillonStatut = ''
                this.editionPrete = true
            } catch (error) {
                if (version !== this.ouvertureVersion) return
                this.erreur = (error.response && error.response.data && error.response.data.message) || 'Impossible de charger ce plan pour le modifier.'
            } finally {
                if (version === this.ouvertureVersion) this.chargement = false
            }
        },
        choisirAdmission(id) {
            if (String(this.admissionId) === String(id)) return
            this.admissionId = id
            this.chambreId = ''
            this.observationId = ''
        },
        restaurerBrouillon(data) {
            this.admissionId = data.admissionId || ''
            this.chambreId = data.chambreId || ''
            this.observationId = data.observationId || ''
            this.joursChambre = data.joursChambre || 1
            this.joursObservation = data.joursObservation || 1
            this.lignes = Array.isArray(data.lignes) && data.lignes.length ? data.lignes.map(ligne => ({ ...nouvelleLigne(), ...ligne, listeOuverte: false, survolIndex: -1 })) : [nouvelleLigne()]
            this.actes = Array.isArray(data.actes) ? data.actes.map(ligne => ({ ...nouvelActe(), ...ligne })) : []
            this.consigne = data.consigne || ''
            this.brouillonStatut = 'Brouillon récupéré'
        },
        contientSaisie() {
            return Boolean(this.admissionId || this.consigne.trim() || this.actes.some(ligne => ligne.acteId)
                || this.lignes.some(ligne => ligne.medicamentId || ligne.horsCatalogue || ligne.recherche.trim()))
        },
        appliquerModele(modele) {
            if (!modele) return
            if (this.contientSaisie() && !window.confirm('Ce protocole remplacera la saisie en cours. Continuer ?')) return
            const config = modele.configuration || {}
            // Ne conserver ni prix ni nom du modèle : les catalogues du jour font foi.
            this.choisirAdmission(config.admissionId || '')
            this.lignes = Array.isArray(config.lignes) && config.lignes.length
                ? config.lignes.map(ligne => {
                    const medicament = this.medicaments.find(item => String(item.id) === String(ligne.medicamentId))
                    return { ...nouvelleLigne(), ...ligne, recherche: medicament ? medicament.libelle : '' }
                }) : [nouvelleLigne()]
            this.actes = Array.isArray(config.actes) ? config.actes.map(ligne => ({ ...nouvelActe(), ...ligne })) : []
            this.consigne = config.consigne || ''
            this.modeleId = modele.id
            this.rechercheModele = ''
        },
        async enregistrerFavori() {
            this.favoriErreur = ''
            const nom = this.nomFavori.trim()
            if (!nom) { this.favoriErreur = 'Nommez ce favori.'; return }
            const lignes = this.lignes.filter(ligne => ligne.medicamentId).map(ligne => ({
                medicamentId: ligne.medicamentId, voieId: ligne.voieId, dose: ligne.dose, doseClinique: ligne.doseClinique,
                rythmeMode: ligne.rythmeMode, intervalleValeur: ligne.intervalleValeur, intervalleUnite: ligne.intervalleUnite,
                dureeValeur: ligne.dureeValeur, dureeUnite: ligne.dureeUnite, debutMode: ligne.debutMode,
                debutValeur: ligne.debutValeur, debutUnite: ligne.debutUnite
            }))
            const actes = this.actes.filter(ligne => ligne.acteId).map(ligne => ({ acteId: ligne.acteId, nombre: ligne.nombre }))
            if (!lignes.length && !actes.length) { this.favoriErreur = 'Sélectionnez au moins un médicament du catalogue ou un acte.'; return }
            if (lignes.some(ligne => !ligne.voieId || !Number(ligne.dose))) { this.favoriErreur = 'Précisez la dose et la voie de chaque médicament.'; return }
            this.favoriEnregistrement = true
            try {
                const resultat = await protocolesTypesApi.ajouterPersonnel({ nom, configuration: {
                    admissionId: this.admissionId || null, lignes, actes, consigne: this.favoriConsigne ? this.consigne : ''
                } })
                this.modeles = resultat.data || this.modeles
                this.modeleId = resultat.id
                this.favoriOuvert = false
                this.nomFavori = ''
                this.favoriConsigne = false
            } catch (error) {
                this.favoriErreur = (error.response && error.response.data && error.response.data.message) || 'Impossible d’enregistrer ce favori.'
            } finally { this.favoriEnregistrement = false }
        },
        async supprimerFavori(modele) {
            if (!modele.personnel || !window.confirm(`Supprimer le favori « ${modele.nom} » ?`)) return
            try {
                await protocolesTypesApi.supprimerPersonnel(modele.id)
                this.modeles = this.modeles.filter(item => String(item.id) !== String(modele.id))
                if (String(this.modeleId) === String(modele.id)) this.modeleId = ''
            } catch (error) { this.favoriErreur = 'Impossible de supprimer ce favori.' }
        },
        planifierBrouillon() {
            // Une modification ne remplace pas le brouillon d'un nouveau plan.
            if (!this.brouillonPret || !this.ouvert || this.enregistre || this.remplaceProtocoleId) return
            this.brouillonVersion++
            this.brouillonStatut = 'Modifications en attente…'
            if (this.brouillonTimer) clearTimeout(this.brouillonTimer)
            this.brouillonTimer = setTimeout(() => { this.sauvegarderBrouillon() }, 350)
        },
        async sauvegarderBrouillon() {
            if (!this.brouillonPret || this.enregistre || this.remplaceProtocoleId) return true
            if (this.brouillonPromise) return this.brouillonPromise
            this.brouillonPromise = (async () => {
                try {
                    while (this.brouillonSauveVersion < this.brouillonVersion) {
                        const version = this.brouillonVersion
                        const copie = JSON.parse(JSON.stringify(this.brouillon))
                        await protocoleApi.sauverBrouillonPlanSoins(this.consultation.id, this.evolution_id, copie)
                        if (this.ressourcesPretes) this.$set(this.ressourcesPretes, 7, copie)
                        this.brouillonSauveVersion = version
                    }
                    this.brouillonErreur = ''
                    this.brouillonStatut = 'Brouillon enregistré'
                    return true
                } catch (error) {
                    this.brouillonErreur = 'Le brouillon n’a pas pu être enregistré. Gardez cette fenêtre ouverte et réessayez.'
                    return false
                } finally { this.brouillonPromise = null }
            })()
            return this.brouillonPromise
        },
        async fermer() {
            if (this.enregistrement) return
            if (this.enregistre) {
                if (this.caisseEnAttente && !window.confirm('La caisse n’est pas encore à jour pour ce plan. Fermer quand même ?')) return
                this.ouvertureVersion++; this.ouvert = false; return
            }
            if (this.brouillonEnConflit) { this.brouillonErreur = 'Choisissez de conserver votre saisie ou de restaurer le brouillon avant de fermer.'; return }
            if (this.brouillonTimer) clearTimeout(this.brouillonTimer)
            if (this.brouillonVersion > this.brouillonSauveVersion && !(await this.sauvegarderBrouillon())) return
            this.ouvertureVersion++
            this.ouvert = false
        },
        ajouterLigne() { this.lignes.push(nouvelleLigne()) },
        ajouterActe() { this.actes.push(nouvelActe()) },
        acteDe(ligne) { return this.actesDisponibles.find(item => String(item.id) === String(ligne.acteId)) },
        rechercherMedicament(ligne) {
            // La saisie seule ne vaut ni sélection catalogue ni prescription libre.
            ligne.medicamentId = ''
            ligne.horsCatalogue = false
            ligne.doseClinique = ''
            ligne.survolIndex = -1
            ligne.listeOuverte = true
        },
        ouvrirSuggestions(ligne) {
            ligne.survolIndex = -1
            ligne.listeOuverte = true
        },
        medicamentsCorrespondants(ligne) {
            const normaliser = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase()
            const terme = normaliser(ligne.recherche.trim())
            return this.medicaments.filter(item => !terme || normaliser(item.libelle).includes(terme))
        },
        medicamentsEnStock(ligne) {
            return this.medicamentsCorrespondants(ligne).filter(item => Number(item.qte) > 0).slice(0, 10)
        },
        medicamentsHorsStock(ligne) {
            return this.medicamentsCorrespondants(ligne).filter(item => Number(item.qte) <= 0).slice(0, 10)
        },
        optionsMedicament(ligne) {
            const options = this.medicamentsEnStock(ligne).concat(this.medicamentsHorsStock(ligne))
                .map(medicament => ({ type: 'catalogue', medicament }))
            if (ligne.recherche.trim().length >= 2) options.push({ type: 'libre' })
            return options
        },
        selectionnerMedicament(ligne, medicament) {
            if (!medicament) return
            ligne.medicamentId = medicament.id
            ligne.recherche = medicament.libelle
            ligne.horsCatalogue = false
            ligne.doseClinique = ''
            ligne.listeOuverte = false
            ligne.survolIndex = -1
        },
        prescrireHorsCatalogue(ligne) {
            if (ligne.recherche.trim().length < 2) return
            ligne.medicamentId = ''
            ligne.recherche = ligne.recherche.trim()
            ligne.horsCatalogue = true
            ligne.doseClinique = ''
            ligne.listeOuverte = false
            ligne.survolIndex = -1
        },
        naviguerMedicaments(ligne, sens) {
            const propositions = this.optionsMedicament(ligne)
            if (!propositions.length) return
            ligne.listeOuverte = true
            ligne.survolIndex = ligne.survolIndex < 0
                ? (sens > 0 ? 0 : propositions.length - 1)
                : (ligne.survolIndex + sens + propositions.length) % propositions.length
        },
        validerMedicamentClavier(ligne) {
            if (!ligne.listeOuverte) return
            const choix = this.optionsMedicament(ligne)[ligne.survolIndex]
            if (!choix) return
            if (choix.type === 'libre') this.prescrireHorsCatalogue(ligne)
            else this.selectionnerMedicament(ligne, choix.medicament)
        },
        medicamentDe(ligne) { return this.medicaments.find(item => String(item.id) === String(ligne.medicamentId)) },
        uniteDose(ligne) {
            const medicament = this.medicamentDe(ligne)
            return medicament && medicament.forme && (medicament.forme.abreviation || medicament.forme.libelle) || 'unité'
        },
        doseAutomatique(ligne) {
            try { return doseCliniqueParPrise(this.medicamentDe(ligne), ligne.dose) }
            catch (error) { return '' }
        },
        voieDe(ligne) { return this.voies.find(item => String(item.id) === String(ligne.voieId)) },
        bilan(ligne) {
            try {
                const prises = minutesDeSoin(ligne).length
                return { prises, quantite: quantiteTotale(ligne.dose, prises) }
            } catch (error) { return { prises: 0, quantite: 0 } }
        },
        stockLabel(medicament) {
            if (!medicament || medicament.etat_stock === 'rupture') return 'Non disponible en stock'
            if (medicament.etat_stock === 'faible') return `${this.formatNumberCompact(medicament.qte)} en stock · stock faible`
            return `${this.formatNumberCompact(medicament.qte)} en stock`
        },
        async enregistrer() {
            if (this.enregistrement || this.enregistre) return
            this.erreur = ''
            if (!this.brouillonPret) return
            // Une modification n'est enregistrable qu'une fois le plan d'origine chargé.
            if (this.remplaceProtocoleId && !this.editionPrete) return
            const auth = this.$store.getters['authInfo'] || {}
            const contexte = {
                consultation_id: Number(this.consultation.id), evolution_id: Number(this.evolution_id),
                patient_id: Number(this.patient.id), user_id: Number(auth.id),
                user: { nom_medecin: this.user_info.user, titre: this.user_info.titre },
                can_set_pac: false,
                info_patient: {
                    nom_patient: [this.patient.nom, this.patient.prenoms].filter(Boolean).join(' '),
                    sexe: Number(this.patient.sexe_id) === 1 ? 'M' : 'F',
                    telephone: this.patient.telephone_mobile, num_dossier: this.patient.code, age: this.patient.age
                },
                info_caissier: { nom_caissier: auth.name, titre: auth.titre }
            }
            let payload
            try {
                payload = construirePlanExpress({
                    admission: this.admission, chambre: '', observation: this.observation || '',
                    joursChambre: this.admission && Number(this.admission.is_with_room) === 1 ? Number(this.joursChambre) : '',
                    joursObservation: this.observation ? Number(this.joursObservation) : '',
                    lignes: this.lignes.filter(ligne => ligne.medicamentId || ligne.recherche.trim()).map(ligne => ({
                        medicament: this.medicamentDe(ligne), horsCatalogue: ligne.horsCatalogue, nomLibre: ligne.recherche.trim(), voie: this.voieDe(ligne), dose: ligne.dose, doseClinique: ligne.doseClinique,
                        rythmeMode: ligne.rythmeMode, intervalleValeur: ligne.intervalleValeur, intervalleUnite: ligne.intervalleUnite,
                        dureeValeur: ligne.dureeValeur, dureeUnite: ligne.dureeUnite, debutMode: ligne.debutMode,
                        debutValeur: ligne.debutValeur, debutUnite: ligne.debutUnite
                    })),
                    actes: this.actes.map(ligne => ({ acte: this.acteDe(ligne), nombre: ligne.nombre })),
                    consigne: this.consigne, contexte
                })
                payload.modele_id = this.modeleId || null
                if (this.remplaceProtocoleId) payload.remplace_protocole_id = this.remplaceProtocoleId
            } catch (error) { this.erreur = error.message; return }

            this.enregistrement = true
            try {
                if (this.brouillonTimer) clearTimeout(this.brouillonTimer)
                if (this.brouillonPromise) await this.brouillonPromise
                const response = await protocoleApi.enregistrerPlanSoinsExpress(payload)
                if (!response || !response.success) throw new Error(response && response.message ? response.message : 'Le plan n’a pas été enregistré.')
                // Le protocole est créé : un nouvel essai ne doit jamais le dupliquer si la caisse échoue.
                this.enregistre = true
                this.enregistrement = false
                this.transmissionCaisse = true
                if (this.brouillonTimer) clearTimeout(this.brouillonTimer)
                if (!this.remplaceProtocoleId) {
                    try { await protocoleApi.supprimerBrouillonPlanSoins(this.consultation.id, this.evolution_id) }
                    catch (error) { this.brouillonErreur = 'Plan enregistré, mais l’ancien brouillon n’a pas pu être effacé.' }
                    if (this.ressourcesPretes) this.$set(this.ressourcesPretes, 7, null)
                }
                this.$emit('plan-saved')
                const ops = Array.isArray(response.finance_ops) ? response.finance_ops : []
                const anciennes = Array.isArray(response.operations_remplacees) ? response.operations_remplacees : []
                if (await this.synchroniserCaisse(anciennes, ops)) this.terminerAvecSucces()
            } catch (error) {
                this.erreur = (error.response && error.response.data && error.response.data.message) || error.message || 'Impossible d’enregistrer le plan.'
                this.planifierBrouillon()
            } finally { this.enregistrement = false; this.transmissionCaisse = false }
        },
        // Retire d'abord de la caisse les lignes du plan remplacé, puis transmet les nouvelles.
        // Tant que ce n'est pas complet, le reste à faire est conservé pour « Réessayer ».
        async synchroniserCaisse(retirer, ajouter) {
            this.transmissionCaisse = true
            try {
                if (retirer.length) {
                    try { await finance.removeAllItemsFromCaisse({ operations: retirer }) }
                    catch (error) {
                        this.caisseEnAttente = { retirer, ajouter }
                        this.erreur = 'Le plan est modifié, mais ses anciennes lignes n’ont pas pu être retirées de la caisse. Réessayez la mise à jour de la caisse avant tout encaissement.'
                        return false
                    }
                }
                const resultats = await Promise.all(ajouter.map(async op => {
                    try {
                        const facturation = await finance.storeLigneOperation({
                            ...op, duree_forfait_hospi_est_expire: (this.info_assurance_patient || {}).duree_forfait_hospi_est_expire
                        }, { timeout: 12000 })
                        return !!facturation.success
                    } catch (error) { return false }
                }))
                const echecs = ajouter.filter((op, index) => !resultats[index])
                if (echecs.length) {
                    this.caisseEnAttente = { retirer: [], ajouter: echecs }
                    this.erreur = 'Le plan est enregistré, mais sa transmission à la caisse est incomplète. Vérifiez la caisse avant toute nouvelle saisie.'
                    return false
                }
                this.caisseEnAttente = null
                this.erreur = ''
                return true
            } finally { this.transmissionCaisse = false }
        },
        async reessayerCaisse() {
            const attente = this.caisseEnAttente
            if (!attente || this.transmissionCaisse) return
            if (await this.synchroniserCaisse(attente.retirer, attente.ajouter)) this.terminerAvecSucces()
        },
        terminerAvecSucces() {
            this.ouvert = false
            this.successToast(this.remplaceProtocoleId
                ? 'Plan de soins modifié. La caisse et la planification sont à jour.'
                : 'Plan de soins enregistré. La planification est visible dans le dossier.')
        }
    }
}
